import { getQuickcamCoinStage } from '../../environments';

export type QccContractChainKey =
  | 'base'
  | 'bnb'
  | 'polygon'
  | 'optimism'
  | 'arbitrum'
  | 'avalanche';

type ChainNetworkConfig = {
  chainId: number;
  rpcUrls: string[];
  tokenAddress: string;
};

type ChainEntry = {
  label: string;
  mainnet: ChainNetworkConfig;
  testnet: ChainNetworkConfig;
};

const QCC_TOKEN_ADDRESSES = {
  baseMainnet: '0x4e96d27c2f1b8098349952784d6fecb52b669321',
  baseSepolia: '0xc6fe3521644e0f5439fe345f64d372cd6ed37491',
  bnbMainnet: '0x5e0004a3912cac14db8697145912ceda6f7c5771',
  bnbTestnet: '0x9082fc835ae29aee1c0ddac73604d832a9d68189',
  polygonMainnet: '0x5e0004a3912cac14db8697145912ceda6f7c5771',
  polygonAmoy: '0x2defa39e7dfa5c8532fe0bda761571bf15d4623b',
  optimismMainnet: '0x5e0004a3912cac14db8697145912ceda6f7c5771',
  optimismSepolia: '0x76a3dce26437bad35031207b9162c4951ec8cf47',
  arbitrumMainnet: '0x5e0004a3912cac14db8697145912ceda6f7c5771',
  arbitrumSepolia: '0x76a3dce26437bad35031207b9162c4951ec8cf47',
  avalancheMainnet: '0x5e0004a3912cac14db8697145912ceda6f7c5771',
  avalancheSepolia: '0x76A3dCe26437bad35031207B9162c4951EC8cf47',
} as const;

const CHAINS: Record<QccContractChainKey, ChainEntry> = {
  base: {
    label: 'Base',
    mainnet: {
      chainId: 8453,
      rpcUrls: ['https://mainnet.base.org'],
      tokenAddress: QCC_TOKEN_ADDRESSES.baseMainnet,
    },
    testnet: {
      chainId: 84532,
      rpcUrls: ['https://sepolia.base.org'],
      tokenAddress: QCC_TOKEN_ADDRESSES.baseSepolia,
    },
  },
  bnb: {
    label: 'BNB',
    mainnet: {
      chainId: 56,
      rpcUrls: ['https://bsc-dataseed.binance.org'],
      tokenAddress: QCC_TOKEN_ADDRESSES.bnbMainnet,
    },
    testnet: {
      chainId: 97,
      rpcUrls: ['https://data-seed-prebsc-1-s1.binance.org:8545'],
      tokenAddress: QCC_TOKEN_ADDRESSES.bnbTestnet,
    },
  },
  polygon: {
    label: 'Polygon',
    mainnet: {
      chainId: 137,
      rpcUrls: ['https://polygon.drpc.org'],
      tokenAddress: QCC_TOKEN_ADDRESSES.polygonMainnet,
    },
    testnet: {
      chainId: 80002,
      rpcUrls: ['https://polygon-amoy.drpc.org'],
      tokenAddress: QCC_TOKEN_ADDRESSES.polygonAmoy,
    },
  },
  optimism: {
    label: 'Optimism',
    mainnet: {
      chainId: 10,
      rpcUrls: ['https://mainnet.optimism.io'],
      tokenAddress: QCC_TOKEN_ADDRESSES.optimismMainnet,
    },
    testnet: {
      chainId: 11155420,
      rpcUrls: ['https://sepolia.optimism.io'],
      tokenAddress: QCC_TOKEN_ADDRESSES.optimismSepolia,
    },
  },
  arbitrum: {
    label: 'Arbitrum',
    mainnet: {
      chainId: 42161,
      rpcUrls: ['https://arb1.arbitrum.io/rpc'],
      tokenAddress: QCC_TOKEN_ADDRESSES.arbitrumMainnet,
    },
    testnet: {
      chainId: 421614,
      rpcUrls: ['https://sepolia-rollup.arbitrum.io/rpc'],
      tokenAddress: QCC_TOKEN_ADDRESSES.arbitrumSepolia,
    },
  },
  avalanche: {
    label: 'Avalanche',
    mainnet: {
      chainId: 43114,
      rpcUrls: ['https://api.avax.network/ext/bc/C/rpc'],
      tokenAddress: QCC_TOKEN_ADDRESSES.avalancheMainnet,
    },
    testnet: {
      chainId: 43113,
      rpcUrls: ['https://api.avax-test.network/ext/bc/C/rpc'],
      tokenAddress: QCC_TOKEN_ADDRESSES.avalancheSepolia,
    },
  },
};

export function usesQccTestnetContracts(
  href = typeof window !== 'undefined' ? window.location.href : '',
): boolean {
  return getQuickcamCoinStage(href) !== 'prod';
}

export function getQccChainConfig(
  chainKey: QccContractChainKey,
  href = typeof window !== 'undefined' ? window.location.href : '',
): ChainNetworkConfig & { label: string } {
  const entry = CHAINS[chainKey];
  const env = usesQccTestnetContracts(href) ? entry.testnet : entry.mainnet;
  return { ...env, label: entry.label };
}

export function isQccChainSupported(
  chainKey: QccContractChainKey,
  href = typeof window !== 'undefined' ? window.location.href : '',
): boolean {
  const cfg = getQccChainConfig(chainKey, href);
  if (!cfg.tokenAddress) return false;
  return !/^0x0+$/i.test(cfg.tokenAddress);
}

export const QCC_BALANCE_CHAIN_KEYS: QccContractChainKey[] = [
  'base',
  'bnb',
  'polygon',
  'optimism',
  'arbitrum',
  'avalanche',
];
